import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  Select,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormControl,
  FormLabel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Divider,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useBreakpointValue,
  Container
} from '@chakra-ui/react';
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  ChevronDownIcon
} from '@chakra-ui/icons';
import {
  FiMessageSquare,
  FiClock,
  FiUser,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
  FiRefreshCw,
  FiFilter,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiSend
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const ReclamationCard = ({ reclamation, onView, onEdit, onDelete, primaryColor, secondaryColor, textColor, cardBg, user }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'yellow';
      case 'in_progress': return 'blue';
      case 'resolved': return 'green';
      case 'closed': return 'gray';
      default: return 'yellow';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'قيد الانتظار';
      case 'in_progress': return 'قيد المعالجة';
      case 'resolved': return 'تم الحل';
      case 'closed': return 'مغلقة';
      default: return 'قيد الانتظار';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      default: return 'blue';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'منخفضة';
      case 'medium': return 'متوسطة';
      case 'high': return 'عالية';
      case 'urgent': return 'عاجلة';
      default: return 'متوسطة';
    }
  };

  return (
    <motion.div variants={fadeIn}>
      <Card
        bg={cardBg}
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.200"
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "lg",
          borderColor: primaryColor
        }}
        transition="all 0.3s ease"
        overflow="hidden"
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={1} flex="1">
              <Heading size="md" color={secondaryColor} fontFamily="Tajawal, sans-serif">
                {reclamation.title || 'شكوى بدون عنوان'}
              </Heading>
              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {reclamation.description || 'لا يوجد وصف متاح'}
              </Text>
            </VStack>
            <VStack spacing={2} align="flex-end">
              <Badge
                colorScheme={getStatusColor(reclamation.status)}
                variant="subtle"
                borderRadius="full"
                px={3}
                py={1}
              >
                {getStatusText(reclamation.status)}
              </Badge>
              <Badge
                colorScheme={getPriorityColor(reclamation.priority)}
                variant="outline"
                borderRadius="full"
                px={2}
                py={1}
                fontSize="xs"
              >
                {getPriorityText(reclamation.priority)}
              </Badge>
            </VStack>
          </Flex>
        </CardHeader>

        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            {/* Reclamation Info */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiUser} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {reclamation.userName || user.username || 'غير محدد'}
                  </Text>
                </HStack>
                <HStack>
                  <Icon as={FiClock} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {reclamation.createdAt ?
                      format(new Date(reclamation.createdAt), 'dd/MM/yyyy', { locale: ar }) :
                      'غير محدد'}
                  </Text>
                </HStack>
              </HStack>

              {reclamation.category && (
                <HStack>
                  <Icon as={FiFileText} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    الفئة: {reclamation.category}
                  </Text>
                </HStack>
              )}

              {reclamation.response && (
                <Box
                  bg="blue.50"
                  p={3}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="sm" color="blue.700" fontWeight="medium">
                    الرد: {reclamation.response}
                  </Text>
                </Box>
              )}
            </VStack>

            <Divider />

            {/* Action Buttons */}
            <HStack spacing={2}>
              <Button
                flex="1"
                variant="outline"
                borderColor="gray.300"
                _hover={{ borderColor: primaryColor, color: primaryColor }}
                leftIcon={<FiEye />}
                onClick={() => onView(reclamation)}
                fontFamily="Tajawal, sans-serif"
                size="sm"
              >
                عرض التفاصيل
              </Button>

             
              {(user?.isAdmin || reclamation.userId === user?._id) && (
                <IconButton
                  aria-label="حذف"
                  icon={<FiTrash2 />}
                  variant="outline"
                  borderColor="red.300"
                  color="red.500"
                  _hover={{ borderColor: "red.500", bg: "red.50" }}
                  onClick={() => onDelete(reclamation)}
                  size="sm"
                />
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </motion.div>
  );
};

const ReclamationsContent = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });

  // Platform colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [reclamations, setReclamations] = useState([]);
  const [filteredReclamations, setFilteredReclamations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Modal states
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium'
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0
  });

  useEffect(() => {
    fetchReclamations();
  }, []);

  useEffect(() => {
    filterReclamations();
  }, [reclamations, searchTerm, filterStatus, filterPriority]);

  const fetchReclamations = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/reclamations/my');

      // Fix: Extract reclamations array from response data
      const reclamationData = response.data?.reclamations || [];

      // Ensure reclamationData is always an array
      const safeReclamationData = Array.isArray(reclamationData) ? reclamationData : [];

      setReclamations(safeReclamationData);
      calculateStats(safeReclamationData);
    } catch (error) {
      console.error('خطأ في جلب الشكاوى:', error);
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'فشل في تحميل قائمة الشكاوى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      // Set empty array on error to prevent map function errors
      setReclamations([]);
      setFilteredReclamations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (reclamationData) => {
    // Ensure reclamationData is an array before calculating stats
    const safeData = Array.isArray(reclamationData) ? reclamationData : [];

    const total = safeData.length;
    const pending = safeData.filter(r => r.status === 'pending').length;
    const inProgress = safeData.filter(r => r.status === 'in_progress').length;
    const resolved = safeData.filter(r => r.status === 'resolved').length;
    const closed = safeData.filter(r => r.status === 'closed').length;

    setStats({
      total,
      pending,
      inProgress,
      resolved,
      closed
    });
  };

  const filterReclamations = () => {
    // Ensure reclamations is always an array before filtering
    let filtered = Array.isArray(reclamations) ? reclamations : [];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(reclamation =>
        reclamation.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reclamation.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reclamation.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(reclamation => reclamation.status === filterStatus);
    }

    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(reclamation => reclamation.priority === filterPriority);
    }

    setFilteredReclamations(filtered);
  };

  const handleCreateReclamation = async () => {
    try {
      const response = await axiosInstance.post('/reclamations', formData);

      toast({
        title: 'تم إرسال الشكوى',
        description: 'تم إرسال شكواك بنجاح وسيتم الرد عليها قريباً',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setFormData({ title: '', description: '', category: '', priority: 'medium' });
      onCreateClose();
      fetchReclamations();
    } catch (error) {
      console.error('خطأ في إرسال الشكوى:', error);
      toast({
        title: 'خطأ في الإرسال',
        description: 'فشل في إرسال الشكوى، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleViewReclamation = (reclamation) => {
    setSelectedReclamation(reclamation);
    onViewOpen();
  };

  const handleEditReclamation = (reclamation) => {
    setSelectedReclamation(reclamation);
    setFormData({
      title: reclamation.title || '',
      description: reclamation.description || '',
      category: reclamation.category || '',
      priority: reclamation.priority || 'medium'
    });
    onEditOpen();
  };

  const handleDeleteReclamation = (reclamation) => {
    setSelectedReclamation(reclamation);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/reclamations/${selectedReclamation._id}`);

      toast({
        title: 'تم حذف الشكوى',
        description: 'تم حذف الشكوى بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onDeleteClose();
      fetchReclamations();
    } catch (error) {
      console.error('خطأ في حذف الشكوى:', error);
      toast({
        title: 'خطأ في الحذف',
        description: 'فشل في حذف الشكوى، يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Tajawal, sans-serif">جاري تحميل الشكاوى...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <Sidebar />

        <Box
          flex="1"
          mr={contentMarginRight}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading
                      as="h1"
                      size="xl"
                      color={secondaryColor}
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="700"
                    >
                      الشكاوى والاقتراحات 📝
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      أرسل شكواك أو اقتراحك وسنقوم بالرد عليك في أقرب وقت
                    </Text>
                  </VStack>

                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      borderColor="gray.300"
                      _hover={{ borderColor: primaryColor, color: primaryColor }}
                      leftIcon={<FiRefreshCw />}
                      onClick={fetchReclamations}
                      fontFamily="Tajawal, sans-serif"
                    >
                      تحديث
                    </Button>
                    <Button
                      bg={primaryColor}
                      color={secondaryColor}
                      _hover={{ bg: `${primaryColor}dd` }}
                      leftIcon={<FiPlus />}
                      onClick={onCreateOpen}
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="bold"
                    >
                      شكوى جديدة
                    </Button>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200">
                  <CardBody p={4}>
                    <Stat>
                      <StatLabel color="gray.600" fontSize="sm">المجموع</StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl">{stats.total}</StatNumber>
                      <StatHelpText color="gray.500" fontSize="xs">إجمالي الشكاوى</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="yellow.200">
                  <CardBody p={4}>
                    <Stat>
                      <StatLabel color="yellow.600" fontSize="sm">قيد الانتظار</StatLabel>
                      <StatNumber color="yellow.600" fontSize="2xl">{stats.pending}</StatNumber>
                      <StatHelpText color="yellow.500" fontSize="xs">في انتظار المراجعة</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="blue.200">
                  <CardBody p={4}>
                    <Stat>
                      <StatLabel color="blue.600" fontSize="sm">قيد المعالجة</StatLabel>
                      <StatNumber color="blue.600" fontSize="2xl">{stats.inProgress}</StatNumber>
                      <StatHelpText color="blue.500" fontSize="xs">يتم العمل عليها</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="green.200">
                  <CardBody p={4}>
                    <Stat>
                      <StatLabel color="green.600" fontSize="sm">تم الحل</StatLabel>
                      <StatNumber color="green.600" fontSize="2xl">{stats.resolved}</StatNumber>
                      <StatHelpText color="green.500" fontSize="xs">تم حل المشكلة</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.300">
                  <CardBody p={4}>
                    <Stat>
                      <StatLabel color="gray.600" fontSize="sm">مغلقة</StatLabel>
                      <StatNumber color="gray.600" fontSize="2xl">{stats.closed}</StatNumber>
                      <StatHelpText color="gray.500" fontSize="xs">تم إغلاق الشكوى</StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={4}>
                <Flex
                  gap={4}
                  flexDirection={{ base: 'column', md: 'row' }}
                  align={{ base: 'stretch', md: 'center' }}
                >
                  <InputGroup flex="1">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.600" />
                    </InputLeftElement>
                    <Input
                      placeholder="البحث في الشكاوى..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                      fontFamily="Tajawal, sans-serif"
                    />
                  </InputGroup>

                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                    fontFamily="Tajawal, sans-serif"
                    maxW={{ base: 'full', md: '200px' }}
                  >
                    <option value="all">جميع الحالات</option>
                    <option value="pending">قيد الانتظار</option>
                    <option value="in_progress">قيد المعالجة</option>
                    <option value="resolved">تم الحل</option>
                    <option value="closed">مغلقة</option>
                  </Select>

                  <Select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                    fontFamily="Tajawal, sans-serif"
                    maxW={{ base: 'full', md: '200px' }}
                  >
                    <option value="all">جميع الأولويات</option>
                    <option value="low">منخفضة</option>
                    <option value="medium">متوسطة</option>
                    <option value="high">عالية</option>
                    <option value="urgent">عاجلة</option>
                  </Select>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Reclamations Grid */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {Array.isArray(filteredReclamations) && filteredReclamations.length > 0 ? (
              <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={6}>
                {filteredReclamations.map((reclamation) => (
                  <ReclamationCard
                    key={reclamation._id}
                    reclamation={reclamation}
                    onView={handleViewReclamation}
                    onEdit={handleEditReclamation}
                    onDelete={handleDeleteReclamation}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    textColor={textColor}
                    cardBg={cardBg}
                    user={user}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200">
                  <CardBody p={8}>
                    <VStack spacing={4}>
                      <Icon as={FiMessageSquare} boxSize={12} color="gray.600" />
                      <Heading size="md" color="gray.600" fontFamily="Tajawal, sans-serif">
                        لا توجد شكاوى
                      </Heading>
                      <Text color="gray.500" textAlign="center" fontFamily="Tajawal, sans-serif">
                        لم يتم العثور على أي شكاوى. يمكنك إضافة شكوى جديدة من خلال النقر على زر "شكوى جديدة"
                      </Text>
                      <Button
                        bg={primaryColor}
                        color={secondaryColor}
                        _hover={{ bg: `${primaryColor}dd` }}
                        leftIcon={<FiPlus />}
                        onClick={onCreateOpen}
                        fontFamily="Tajawal, sans-serif"
                        fontWeight="bold"
                      >
                        شكوى جديدة
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </motion.div>
            )}
          </motion.div>

          {/* Create Reclamation Modal */}
          <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
            <ModalOverlay />
            <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
              <ModalHeader>إضافة شكوى جديدة</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>عنوان الشكوى</FormLabel>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="أدخل عنوان الشكوى"
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>وصف الشكوى</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="اشرح تفاصيل الشكوى بوضوح"
                      rows={4}
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>الفئة</FormLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="فئة الشكوى (اختياري)"
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="technical">مشاكل تقنية</option>
                      <option value="content">مشاكل في المحتوى</option>
                      <option value="payment">مشاكل في الدفع</option>
                      <option value="instructor">مشاكل مع المدرس</option>
                      <option value="course">مشاكل في الدورة</option>
                      <option value="account">مشاكل في الحساب</option>
                      <option value="suggestion">اقتراح</option>
                      <option value="other">أخرى</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>الأولوية</FormLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="low">منخفضة</option>
                      <option value="medium">متوسطة</option>
                      <option value="high">عالية</option>
                      <option value="urgent">عاجلة</option>
                    </Select>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onCreateClose} fontFamily="Tajawal, sans-serif">
                  إلغاء
                </Button>
                <Button
                  bg={primaryColor}
                  color={secondaryColor}
                  _hover={{ bg: `${primaryColor}dd` }}
                  onClick={handleCreateReclamation}
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="bold"
                >
                  إرسال الشكوى
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* View Reclamation Modal */}
          <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
            <ModalOverlay />
            <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
              <ModalHeader>تفاصيل الشكوى</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {selectedReclamation && (
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontWeight="bold" mb={2}>العنوان:</Text>
                      <Text>{selectedReclamation.title}</Text>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" mb={2}>الوصف:</Text>
                      <Text>{selectedReclamation.description}</Text>
                    </Box>

                    <HStack>
                      <Box flex="1">
                        <Text fontWeight="bold" mb={2}>الحالة:</Text>
                        <Badge
                          colorScheme={
                            selectedReclamation.status === 'pending' ? 'yellow' :
                              selectedReclamation.status === 'in_progress' ? 'blue' :
                                selectedReclamation.status === 'resolved' ? 'green' : 'gray'
                          }
                        >
                          {selectedReclamation.status === 'pending' ? 'قيد الانتظار' :
                            selectedReclamation.status === 'in_progress' ? 'قيد المعالجة' :
                              selectedReclamation.status === 'resolved' ? 'تم الحل' : 'مغلقة'}
                        </Badge>
                      </Box>

                      <Box flex="1">
                        <Text fontWeight="bold" mb={2}>الأولوية:</Text>
                        <Badge
                          colorScheme={
                            selectedReclamation.priority === 'low' ? 'green' :
                              selectedReclamation.priority === 'medium' ? 'yellow' :
                                selectedReclamation.priority === 'high' ? 'orange' : 'red'
                          }
                        >
                          {selectedReclamation.priority === 'low' ? 'منخفضة' :
                            selectedReclamation.priority === 'medium' ? 'متوسطة' :
                              selectedReclamation.priority === 'high' ? 'عالية' : 'عاجلة'}
                        </Badge>
                      </Box>
                    </HStack>

                    {selectedReclamation.category && (
                      <Box>
                        <Text fontWeight="bold" mb={2}>الفئة:</Text>
                        <Text>{selectedReclamation.category}</Text>
                      </Box>
                    )}

                    {selectedReclamation.response && (
                      <Box>
                        <Text fontWeight="bold" mb={2}>الرد:</Text>
                        <Box
                          bg="blue.50"
                          p={3}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="blue.200"
                        >
                          <Text color="blue.700">{selectedReclamation.response}</Text>
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Text fontWeight="bold" mb={2}>تاريخ الإنشاء:</Text>
                      <Text>
                        {selectedReclamation.createdAt ?
                          format(new Date(selectedReclamation.createdAt), 'dd/MM/yyyy HH:mm', { locale: ar }) :
                          'غير محدد'}
                      </Text>
                    </Box>
                  </VStack>
                )}
              </ModalBody>

              <ModalFooter>
                <Button onClick={onViewClose} fontFamily="Tajawal, sans-serif">
                  إغلاق
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Edit Reclamation Modal */}
          <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
            <ModalOverlay />
            <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
              <ModalHeader>تعديل الشكوى</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>عنوان الشكوى</FormLabel>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="أدخل عنوان الشكوى"
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>وصف الشكوى</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="اشرح تفاصيل الشكوى بوضوح"
                      rows={4}
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>الفئة</FormLabel>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="فئة الشكوى (اختياري)"
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>الأولوية</FormLabel>
                    <Select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="low">منخفضة</option>
                      <option value="medium">متوسطة</option>
                      <option value="high">عالية</option>
                      <option value="urgent">عاجلة</option>
                    </Select>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onEditClose} fontFamily="Tajawal, sans-serif">
                  إلغاء
                </Button>
                <Button
                  bg={primaryColor}
                  color={secondaryColor}
                  _hover={{ bg: `${primaryColor}dd` }}
                  onClick={async () => {
                    try {
                      await axiosInstance.put(`/reclamations/${selectedReclamation._id}`, formData);
                      toast({
                        title: 'تم تحديث الشكوى',
                        description: 'تم تحديث الشكوى بنجاح',
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                      });
                      onEditClose();
                      fetchReclamations();
                    } catch (error) {
                      console.error('خطأ في تحديث الشكوى:', error);
                      toast({
                        title: 'خطأ في التحديث',
                        description: 'فشل في تحديث الشكوى، يرجى المحاولة مرة أخرى',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  }}
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="bold"
                >
                  حفظ التغييرات
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
            <ModalOverlay />
            <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
              <ModalHeader>تأكيد الحذف</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>هل أنت متأكد من أنك تريد حذف هذه الشكوى؟ لا يمكن التراجع عن هذا الإجراء.</Text>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onDeleteClose} fontFamily="Tajawal, sans-serif">
                  إلغاء
                </Button>
                <Button
                  colorScheme="red"
                  onClick={confirmDelete}
                  fontFamily="Tajawal, sans-serif"
                >
                  حذف
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </Flex>
    </Box>
  );
};

const Reclamations = () => {
  return (
    <SidebarProvider>
      <ReclamationsContent />
    </SidebarProvider>
  );
};

export default Reclamations;


